import React, { useState, useEffect } from 'react';
import { paymentAPI, receiptAPI, getErrorMessage } from '../services/api';
import { X, Download, CheckCircle, XCircle, AlertCircle, Clock, Send } from 'lucide-react';
import toast from 'react-hot-toast';

const statusConfig = {
  pending: { icon: Clock, color: 'text-yellow-600', bgColor: 'bg-yellow-100' },
  approved: { icon: CheckCircle, color: 'text-green-600', bgColor: 'bg-green-100' },
  rejected: { icon: XCircle, color: 'text-red-600', bgColor: 'bg-red-100' },
  needs_info: { icon: AlertCircle, color: 'text-blue-600', bgColor: 'bg-blue-100' },
};

function isImageReceipt(receipt) {
  if (!receipt) return false;
  const name = receipt.original_filename?.toLowerCase() || '';
  return (
    receipt.file_type === 'image'
    || name.endsWith('.jpg')
    || name.endsWith('.jpeg')
    || name.endsWith('.png')
    || name.endsWith('.webp')
  );
}

export default function PaymentDetail({ paymentId, onClose, onStatusChange }) {
  const [payment, setPayment] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [receiptPreviewUrl, setReceiptPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [newStatus, setNewStatus] = useState('');
  const [reason, setReason] = useState('');

  const loadPayment = async () => {
    const paymentRes = await paymentAPI.getPayment(paymentId);
    setPayment(paymentRes.data);
    setNewStatus(paymentRes.data.status);
    return paymentRes.data;
  };

  useEffect(() => {
    let previewUrl = null;

    const fetchPaymentDetails = async () => {
      setLoading(true);
      setReceipt(null);
      setReceiptPreviewUrl(null);

      try {
        await loadPayment();

        try {
          const receiptRes = await receiptAPI.getReceipt(paymentId);
          setReceipt(receiptRes.data);

          if (isImageReceipt(receiptRes.data)) {
            const blobRes = await receiptAPI.downloadReceipt(receiptRes.data.id);
            previewUrl = URL.createObjectURL(blobRes.data);
            setReceiptPreviewUrl(previewUrl);
          }
        } catch (err) {
          if (err?.response?.status !== 404) {
            console.error('Failed to fetch receipt:', err);
          }
        }
      } catch (err) {
        toast.error(getErrorMessage(err) || 'Failed to load payment details');
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentDetails();

    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [paymentId]);

  const handleStatusUpdate = async () => {
    if (!newStatus) {
      toast.error('Please select a status');
      return;
    }

    setUpdating(true);
    try {
      await paymentAPI.updatePaymentStatus(paymentId, newStatus, {
        rejection_reason: newStatus === 'rejected' ? reason : null,
        info_requested: newStatus === 'needs_info' ? reason : null,
      });

      await loadPayment();
      setShowUpdateForm(false);
      toast.success('Payment status updated');
      onStatusChange?.();
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Failed to update payment status');
    } finally {
      setUpdating(false);
    }
  };

  const handleDownloadReceipt = async () => {
    if (!receipt) return;
    try {
      const res = await receiptAPI.downloadReceipt(receipt.id);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', receipt.original_filename);
      document.body.appendChild(link);
      link.click();
      link.parentElement.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Receipt downloaded');
    } catch (err) {
      toast.error(getErrorMessage(err) || 'Failed to download receipt');
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto" />
        </div>
      </div>
    );
  }

  if (!payment) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <p className="text-red-600">Payment not found</p>
          <button onClick={onClose} className="mt-4 px-4 py-2 bg-blue-500 text-white rounded-lg">
            Close
          </button>
        </div>
      </div>
    );
  }

  const StatusIcon = statusConfig[payment.status]?.icon || Clock;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold">Payment Details</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-6">
          <div className="flex items-center gap-4 p-4 rounded-lg bg-gray-50">
            <div className={`p-3 rounded-lg ${statusConfig[payment.status]?.bgColor}`}>
              <StatusIcon className={`w-6 h-6 ${statusConfig[payment.status]?.color}`} />
            </div>
            <div>
              <p className="text-sm text-gray-600">Status</p>
              <p className="text-xl font-bold capitalize">{payment.status.replace('_', ' ')}</p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 border border-gray-200 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Amount</p>
              <p className="text-2xl font-bold text-gray-900">{payment.amount} Birr</p>
            </div>
            <div className="p-4 border border-gray-200 rounded-lg">
              <p className="text-sm text-gray-600 mb-1">Month</p>
              <p className="text-lg font-semibold text-gray-900">{payment.payment_month}</p>
            </div>
          </div>

          {(payment.parent_name || payment.parent_phone) && (
            <div className="space-y-3 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <h3 className="font-semibold text-gray-900">Parent</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                <div>
                  <label className="text-gray-600">Name</label>
                  <p className="font-medium text-gray-900">{payment.parent_name || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-gray-600">Phone</label>
                  <p className="font-mono text-gray-900">{payment.parent_phone || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-gray-600">Center</label>
                  <p className="font-medium text-gray-900">{payment.center_name || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-gray-600">Type</label>
                  <p className="font-medium text-gray-900 capitalize">{payment.payment_type?.replace('_', ' ') || 'N/A'}</p>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-900">Transaction Details</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <label className="text-gray-600">Reference</label>
                <p className="font-mono text-gray-900">{payment.transaction_reference}</p>
              </div>
              <div>
                <label className="text-gray-600">Payment Phone</label>
                <p className="font-mono text-gray-900">{payment.phone_number}</p>
              </div>
            </div>
          </div>

          {payment.rejection_reason && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
              <strong>Rejection reason:</strong> {payment.rejection_reason}
            </div>
          )}

          {payment.info_requested && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
              <strong>Info requested:</strong> {payment.info_requested}
            </div>
          )}

          {payment.students && payment.students.length > 0 && (
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-900">Students</h3>
              <div className="space-y-2">
                {payment.students.map((student) => (
                  <div key={student.id} className="p-3 bg-gray-50 rounded-lg">
                    <p className="font-medium text-gray-900">{student.name}</p>
                    {student.grade && <p className="text-sm text-gray-600">{student.grade}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {receipt && (
            <div className="space-y-3 p-4 border border-gray-200 rounded-lg">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">Receipt</h3>
                <button
                  onClick={handleDownloadReceipt}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
                >
                  <Download className="w-4 h-4" />
                  Download
                </button>
              </div>
              <p className="font-medium text-gray-900">{receipt.original_filename}</p>
              <p className="text-sm text-gray-600">{(receipt.file_size / 1024).toFixed(2)} KB</p>
              {receiptPreviewUrl && (
                <img
                  src={receiptPreviewUrl}
                  alt="Payment receipt"
                  className="max-h-80 rounded-lg border border-gray-200 object-contain bg-gray-50 w-full"
                />
              )}
            </div>
          )}

          <div className="border-t pt-6">
            {!showUpdateForm ? (
              <button
                onClick={() => setShowUpdateForm(true)}
                className="w-full px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium"
              >
                Update Status
              </button>
            ) : (
              <div className="space-y-3">
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="needs_info">Needs Info</option>
                </select>

                {(newStatus === 'rejected' || newStatus === 'needs_info') && (
                  <textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="Enter reason..."
                    rows="3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                )}

                <div className="flex gap-2">
                  <button
                    onClick={handleStatusUpdate}
                    disabled={updating}
                    className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:opacity-50 font-medium flex items-center justify-center gap-2"
                  >
                    <Send className="w-4 h-4" />
                    {updating ? 'Updating...' : 'Update'}
                  </button>
                  <button
                    onClick={() => setShowUpdateForm(false)}
                    className="flex-1 px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
